import { api } from "../api";
import { openModal, closeModal, modalBody } from "./modal";
import { confirmDialog } from "./confirm-dialog";
import { canWrite, writeOnly, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { Icons } from "../utils/icons";
import { stageLabel } from "../utils/stage";
import type { CourseModel, Curriculum, CurriculumGroup, CurriculumSubject, CurriculumTemplateInfo, Subject } from "../types";
import { esc } from "../utils/html";

/**
 * Lehrplan eines Ausbildungsmodells, eingebettet in die Seite des Modells.
 *
 * Die Verordnungen geben Mindeststunden je Beruf vor, oft für eine ganze Fächergruppe
 * (Physio: Anatomie 240 UE für Bewegungsapparat, Organe und Neuro zusammen). Wie die
 * Schule das verteilt, entscheidet sie selbst. Eine Zeile trägt deshalb alles zu einem
 * Fach in diesem Modell: Gruppe, Stufe, UE der Schule, Pflicht und UE pro Woche.
 *
 * Aufbau: Kopfzeile mit Aktionen, vier Kennzahlen, die Stundentafel. Zeilen öffnen sich per
 * Klick, die Pfeile zum Umsortieren erscheinen beim Überfahren.
 */

let model: CourseModel;
let models: CourseModel[] = [];
let subjects: Subject[] = [];
let curriculum: Curriculum | null = null;
let host: HTMLElement | null = null;
/** Seitenkopf, in dem die Knöpfe des Lehrplans stehen; ohne steht er über der Tafel. */
let actionsHost: HTMLElement | null = null;
/** Aufgeklappte Gruppen. Bei jedem Öffnen der Seite sind alle Gruppen offen. */
let expanded: { modelId: number; ids: Set<number> } | null = null;
let menuListener = false;
let changed: () => void = () => {};

// ── Hilfen ──

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "";
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

/** Zahl aus einem Feld; Tausenderpunkte („2.900") und Komma sind erlaubt. */
function parseUe(raw: string): number | null | "invalid" {
  const v = raw.trim().replace(/\./g, "").replace(",", ".");
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : "invalid";
}

function ueInput(id: string, value: number | null | undefined, attrs = ""): string {
  return `<div class="lp-unit"><input type="text" inputmode="decimal" id="${id}" value="${esc(fmt(value))}" ${attrs} /><span>UE</span></div>`;
}

function statusBadge(schule: number | null, pflicht: number | null): string {
  if (pflicht === null) return "";
  if (schule === null) return `<span class="badge badge-gray">ohne UE Schule</span>`;
  if (schule < pflicht) return `<span class="badge badge-red" title="Die Schule setzt weniger an, als die Verordnung verlangt">${fmt(pflicht - schule)} UE fehlen</span>`;
  if (schule > pflicht) return `<span class="badge badge-blue" title="Die Schule setzt mehr an als verlangt">${fmt(schule - pflicht)} UE darüber</span>`;
  return `<span class="badge badge-green">erfüllt</span>`;
}

/**
 * Hinweis im Fach-Fenster, wenn die Fächer einer Gruppe zusammen über ihrer Pflicht
 * liegen. Darunter oder genau passend bleibt es still: wie die Schule verteilt, ist ihre
 * Sache, und ob ein Fach fertig ist, meldet der Stundenplan.
 */
export function overNote(id: string, groupName: string | undefined, pflichtUe: number | null, sum: number, verb: string): string {
  const over = groupName !== undefined && pflichtUe !== null && sum - pflichtUe > 0.001;
  return `<div class="lp-over-note${over ? "" : " hidden"}" id="${id}">${over
    ? `Die Gruppe „${esc(groupName!)}“ ${verb} ${esc(fmt(sum))} UE, ${esc(fmt(sum - pflichtUe!))} UE mehr als die Pflicht von ${esc(fmt(pflichtUe))} UE.`
    : ""}</div>`;
}

function swatch(color: string): string {
  return `<span class="lp-swatch" style="background:${esc(color)}"></span>`;
}

/** Auswahl der Stufen dieses Modells, mit seinen Bezeichnungen. */
function stageOptions(selected: number | null): string {
  return `<option value="" ${selected === null ? "selected" : ""}>Alle Stufen</option>` +
    Array.from({ length: model.stageCount }, (_, i) => i + 1)
      .map(n => `<option value="${n}" ${selected === n ? "selected" : ""}>${esc(stageLabel(model, n))}</option>`).join("");
}

// ── Aufbau ──

type TopItem = { type: "gruppe"; group: CurriculumGroup } | { type: "fach"; subject: CurriculumSubject };

function topLevel(c: Curriculum): TopItem[] {
  const items: (TopItem & { order: number })[] = [
    ...c.groups.map(group => ({ type: "gruppe" as const, group, order: group.sortOrder })),
    ...c.subjects.filter(s => s.groupId === null).map(subject => ({ type: "fach" as const, subject, order: subject.sortOrder })),
  ];
  return items.sort((a, b) => a.order - b.order);
}

function members(c: Curriculum, groupId: number): CurriculumSubject[] {
  return c.subjects.filter(s => s.groupId === groupId).sort((a, b) => a.sortOrder - b.sortOrder);
}

function sumSchule(list: CurriculumSubject[]): number | null {
  const values = list.map(s => s.schuleUe).filter((v): v is number => v !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) : null;
}

function totals(c: Curriculum) {
  const schule = sumSchule(c.subjects) ?? 0;
  const fest = c.groups.reduce((a, g) => a + (g.pflichtUe ?? 0), 0)
    + c.subjects.filter(s => s.groupId === null).reduce((a, s) => a + (s.pflichtUe ?? 0), 0);
  const frei = c.totalUe !== null ? c.totalUe - fest : null;
  const vergeben = c.subjects.reduce((a, s) => a + (s.poolUe ?? 0), 0);
  const offen = c.poolUe !== null ? c.poolUe - vergeben : null;
  return { schule, fest, frei, vergeben, offen };
}

/** Pool vorhanden und noch nicht ganz verteilt. */
function poolOpen(c: Curriculum): boolean {
  const o = totals(c).offen;
  return o !== null && o > 0.001;
}

function moveButtons(type: "gruppe" | "fach", id: number, first: boolean, last: boolean): string {
  return writeOnly(`<div class="lp-move">
    <button type="button" class="btn-icon" data-move="${type}:${id}" data-dir="-1" title="Nach oben" aria-label="Nach oben"${first ? " disabled" : ""}>${Icons.chevronUp}</button>
    <button type="button" class="btn-icon" data-move="${type}:${id}" data-dir="1" title="Nach unten" aria-label="Nach unten"${last ? " disabled" : ""}>${Icons.chevronDown}</button>
  </div>`);
}

function subjectName(s: CurriculumSubject): string {
  return `${swatch(s.color)}<span class="lp-name">${esc(s.name)}</span>`
    + (s.shortName ? `<span class="lp-short">${esc(s.shortName)}</span>` : "")
    + (s.active ? "" : `<span class="badge badge-gray" style="margin-left:.4rem">inaktiv</span>`);
}

/**
 * Kennzahlen ohne Kacheln: oben Gesamt laut Verordnung und Schule gesamt, darunter ein
 * schmaler Balken über die volle Breite für den Gesamtumfang, in dem die Pflicht der Fächer
 * und Gruppen (Summe der Spalte „Pflicht“, nicht das im Stundenplan Eingeplante) und
 * Pool-Stunden anteilig stehen. Der Rest bleibt leer. Die Legende darunter trägt die
 * Werte und, solange Pool-Stunden offen sind, den Hinweis mit „Pool-Stunden vergeben“.
 */
function renderKpis(c: Curriculum): string {
  const t = totals(c);
  const under = c.totalUe !== null && t.schule < c.totalUe;
  const pool = c.poolUe ?? 0;
  const pct = (v: number) => (c.totalUe ? Math.max(0, Math.min(100, (v / c.totalUe) * 100)) : 0);
  const festPct = pct(t.fest);
  // Liegen fest und Pool zusammen über dem Gesamtumfang, endet der Pool am Balkenende.
  const poolPct = pool > 0 ? Math.min(pct(pool), 100 - festPct) : 0;
  const poolText = t.vergeben > 0
    ? `Aus dem Pool sind noch <strong>${fmt(t.offen)} von ${fmt(c.poolUe)} UE</strong> zu vergeben.`
    : `Dieser Lehrplan hat <strong>${fmt(c.poolUe)} UE Pool-Stunden</strong>, die ihr selbst auf Fächer verteilt.`;
  return `
    <div class="lp-kpi">
      <dl class="lp-kpi-top">
        <div class="lp-figure"><dt>Gesamt laut Verordnung</dt><dd>${c.totalUe !== null ? `${fmt(c.totalUe)} UE` : `<span class="lp-muted">nicht hinterlegt</span>`}${writeOnly(`<button type="button" class="btn-icon lp-figure-edit" data-action="total" title="Gesamtumfang ändern" aria-label="Gesamtumfang ändern">${Icons.pencilSquare}</button>`)}</dd></div>
        <div class="lp-figure lp-figure-end"><dt>Schule gesamt</dt><dd>${fmt(t.schule)} UE${c.totalUe === null ? "" : under
          ? ` <span class="lp-bad">${fmt(c.totalUe - t.schule)} UE unter der Verordnung</span>`
          : ` <span class="lp-good">erfüllt</span>`}</dd></div>
      </dl>
      ${c.totalUe ? `<div class="lp-bar" aria-hidden="true">
        <span class="lp-bar-fest" style="width:${festPct.toFixed(2)}%"></span>${poolPct > 0 ? `<span class="lp-bar-pool" style="width:${poolPct.toFixed(2)}%"></span>` : ""}
      </div>` : ""}
      <div class="lp-kpi-legend">
        <div class="lp-legend-items">
          <span class="lp-legend">${c.totalUe ? `<i class="lp-legend-fest"></i>` : ""}Pflicht laut Verordnung <strong>${fmt(t.fest)} UE</strong></span>
          ${pool > 0
            ? `<span class="lp-legend">${c.totalUe ? `<i class="lp-legend-pool"></i>` : ""}Pool-Stunden <strong>${fmt(c.poolUe)} UE</strong>
                <span class="${poolOpen(c) ? "lp-muted" : "lp-good"}">${poolOpen(c) ? `${fmt(t.vergeben)} vergeben` : "vergeben"}</span></span>`
            : `<span class="lp-legend">zur freien Verteilung <strong>${t.frei !== null ? `${fmt(t.frei)} UE` : `<span class="lp-muted">offen</span>`}</strong></span>`}
        </div>
        ${pool > 0 && poolOpen(c) ? `<div class="lp-pool-inline">
          <span>${poolText}</span>
          ${writeOnly(`<button type="button" class="btn btn-primary btn-sm" data-action="pool">Pool-Stunden vergeben</button>`)}
        </div>` : ""}
      </div>
    </div>`;
}

/** Beim Öffnen alle Gruppen offen; Zu- und Aufklappen hält, solange die Seite offen ist. */
function expandedIds(c: Curriculum): Set<number> {
  if (!expanded || expanded.modelId !== model.id) {
    expanded = { modelId: model.id, ids: new Set(c.groups.map(g => g.id)) };
  }
  return expanded.ids;
}

/** Eine Gruppe aufklappen, etwa nachdem ein Fach in sie aufgenommen wurde. */
function expandGroup(id: number | null) {
  if (id !== null && curriculum) expandedIds(curriculum).add(id);
}

function closeMenus() {
  document.querySelectorAll(".lp-more-panel").forEach(p => p.classList.add("hidden"));
  document.querySelectorAll('[data-action="more"]').forEach(b => b.setAttribute("aria-expanded", "false"));
}

function row(s: CurriculumSubject, member: boolean, last: boolean, first: boolean, lastInLevel: boolean): string {
  return `
    <tr class="lp-row${member ? " lp-member" : ""}${member && last ? " lp-member-last" : ""}" data-entry="${s.id}">
      <td class="lp-fach" title="${esc(s.name)}"><div class="lp-fach-inner">${subjectName(s)}</div></td>
      ${s.stageLabel ? `<td class="lp-stage" title="${esc(s.stageLabel)}">${esc(s.stageLabel)}</td>` : `<td class="lp-stage lp-stage-all">alle Stufen</td>`}
      <td class="lp-num">${s.poolUe ? `<span class="lp-pool-tag" title="Davon ${fmt(s.poolUe)} UE aus dem Pool">+${fmt(s.poolUe)} Pool</span>` : ""}${fmt(s.schuleUe)}</td>
      <td class="lp-num">${member ? "" : fmt(s.pflichtUe)}</td>
      <td class="lp-num lp-muted">${fmt(s.hoursPerWeek)}</td>
      <td class="lp-actions">${moveButtons("fach", s.id, first, lastInLevel)}</td>
    </tr>`;
}

function renderTable(c: Curriculum): string {
  const items = topLevel(c);
  const open = expandedIds(c);
  const rows: string[] = [];
  items.forEach((item, i) => {
    const first = i === 0, last = i === items.length - 1;
    if (item.type === "fach") { rows.push(row(item.subject, false, false, first, last)); return; }
    const g = item.group;
    const list = members(c, g.id);
    const sum = sumSchule(list);
    const isOpen = open.has(g.id);
    rows.push(`
      <tr class="lp-group${isOpen ? "" : " lp-collapsed"}" data-group="${g.id}">
        <td class="lp-fach" title="${esc(g.name)}"><div class="lp-fach-inner">
          <button type="button" class="btn-icon lp-toggle" data-toggle="${g.id}" aria-expanded="${isOpen}" aria-label="${esc(g.name)} ${isOpen ? "einklappen" : "aufklappen"}">${Icons.chevronDown}</button>
          <span class="lp-name">${esc(g.name)}</span><span class="lp-group-meta">${list.length} ${list.length === 1 ? "Fach" : "Fächer"}</span>
        </div></td>
        <td></td>
        <td class="lp-num">${fmt(sum)}</td>
        <td class="lp-num">${fmt(g.pflichtUe)}</td>
        <td></td>
        <td class="lp-actions">${moveButtons("gruppe", g.id, first, last)}</td>
      </tr>`);
    if (!isOpen) return;
    if (list.length === 0) {
      rows.push(`<tr class="lp-member lp-member-empty" data-group="${g.id}"><td colspan="6">Noch keine Fächer in dieser Gruppe${canWrite() ? ", zum Zuordnen die Gruppe anklicken" : ""}.</td></tr>`);
    }
    list.forEach((s, j) => rows.push(row(s, true, j === list.length - 1, j === 0, j === list.length - 1)));
  });

  const t = totals(c);
  return `
    <div class="card lp-card">
      <table class="lp-table">
        <colgroup><col /><col class="lp-col-stage" /><col class="lp-col-num" /><col class="lp-col-num" /><col class="lp-col-num" /><col class="lp-col-actions" /></colgroup>
        <thead><tr>
          <th>Fach</th>
          <th>Stufe</th>
          <th class="lp-num" title="Was eure Schule ansetzt. Ziel für „Fach fertig“ im Stundenplan.">UE Schule</th>
          <th class="lp-num" title="Mindestumfang laut Ausbildungs- und Prüfungsverordnung">Pflicht</th>
          <th class="lp-num" title="Optional, für den Vorschlag des Serienzeitraums im Stundenplan">UE/Woche</th>
          <th class="lp-actions"></th>
        </tr></thead>
        <tbody>${rows.join("")}</tbody>
        <tfoot><tr>
          <td>Summe</td><td></td>
          <td class="lp-num">${fmt(t.schule)}</td>
          <td class="lp-num">${fmt(t.fest)}</td>
          <td colspan="2"></td>
        </tr></tfoot>
      </table>
    </div>`;
}

function renderEmpty(): string {
  const others = models.filter(m => m.id !== model.id && m.curriculum.subjectCount > 0);
  return `
    <div class="lp-empty">
      <h3>Der Lehrplan ist noch leer</h3>
      <p>Am schnellsten geht es mit der Stundentafel aus einer Vorlage${others.length ? " oder als Kopie eines anderen Modells" : ""}. Du kannst Fächer und Gruppen auch einzeln anlegen.</p>
      ${writeOnly(`<div class="lp-empty-actions">
        <button class="btn btn-primary" data-action="template">Vorlage übernehmen</button>
        ${others.length ? `<button class="btn btn-secondary" data-action="copy">Kopieren von …</button>` : ""}
        <button class="btn btn-secondary" data-action="subject">+ Fach</button>
      </div>
      <p class="lp-empty-hint">Viele Fächer auf einmal: im Reiter „Fächer“ markieren und „Ausbildungsmodell zuordnen“, oder per CSV-Import mit der Spalte Ausbildungsmodell.</p>`)}
    </div>`;
}

function render() {
  if (!host || !curriculum) return;
  const c = curriculum;
  const empty = c.groups.length === 0 && c.subjects.length === 0;
  // Ein leerer Lehrplan bringt seine Knöpfe im Leerzustand mit. Seltene Aktionen liegen
  // im Menü „Weitere Aktionen“, die häufigen stehen direkt daneben.
  const buttons = empty ? "" : writeOnly(`
    <div class="lp-more">
      <button type="button" class="btn btn-secondary" data-action="more" aria-haspopup="menu" aria-expanded="false">Weitere Aktionen ${Icons.chevronDown}</button>
      <div class="lp-more-panel hidden" role="menu">
        <button type="button" role="menuitem" data-action="template">Vorlage übernehmen</button>
        <button type="button" role="menuitem" data-action="copy">Kopieren von …</button>
        ${c.poolUe ? `<button type="button" role="menuitem" data-action="pool">Pool-Stunden vergeben</button>` : ""}
      </div>
    </div>
    <button class="btn btn-secondary" data-action="group">+ Fächergruppe</button>
    <button class="btn btn-primary" data-action="subject">+ Fach</button>`);
  if (actionsHost) actionsHost.innerHTML = buttons;
  host.innerHTML = `
    <div class="lp-section-head">
      <div>
        <h2>Lehrplan</h2>
        <p>Welche Fächer in diesem Modell unterrichtet werden, in welcher Stufe und mit wie vielen Unterrichtseinheiten (UE).</p>
      </div>
      ${!actionsHost && buttons ? `<div class="settings-page-actions">${buttons}</div>` : ""}
    </div>
    ${empty ? renderEmpty() : `${renderKpis(c)}${renderTable(c)}
      ${writeOnly(`<div class="lp-foot-actions"><button type="button" class="lp-danger-link" data-action="clear">Lehrplan leeren</button></div>`)}`}`;
}

function apply(next: Curriculum) {
  curriculum = next;
  render();
  changed();
}

// ── Fenster ──

function showModalError(mb: HTMLElement, message: string) {
  const box = mb.querySelector(".alert-error") as HTMLElement | null;
  if (!box) { showToast(message); return; }
  box.textContent = message;
  box.classList.remove("hidden");
}

function modalActions(dangerLabel?: string, saveLabel = "Speichern", cancelLabel = "Abbrechen"): string {
  return `
    <div class="alert alert-error hidden"></div>
    <div class="form-actions">
      ${dangerLabel ? writeOnly(`<button type="button" class="lp-danger-link" id="m-danger">${esc(dangerLabel)}</button>`) : ""}
      <button type="button" class="btn btn-secondary" id="m-cancel">${canWrite() ? esc(cancelLabel) : "Schließen"}</button>
      ${writeOnly(`<button type="button" class="btn btn-primary" id="m-save">${esc(saveLabel)}</button>`)}
    </div>`;
}

function setBusy(mb: HTMLElement, busy: boolean, label = "Speichern") {
  const btn = mb.querySelector("#m-save") as HTMLButtonElement | null;
  if (!btn) return;
  btn.disabled = busy;
  btn.textContent = busy ? `${label}…` : label;
}

function openEntryForm(entry?: CurriculumSubject) {
  const isEdit = !!entry;
  const c = curriculum!;
  const inPlan = new Set(c.subjects.map(s => s.subjectId));
  const available = subjects.filter(s => !inPlan.has(s.id)).sort((a, b) => a.name.localeCompare(b.name, "de"));
  let mode: "vorhanden" | "neu" = available.length > 0 ? "vorhanden" : "neu";

  const picker = isEdit ? `
      <div class="lp-subject-card">
        ${swatch(entry!.color)}<div><strong>${esc(entry!.name)}</strong>${entry!.shortName ? ` <span class="lp-short">${esc(entry!.shortName)}</span>` : ""}
        <div class="lp-muted" style="font-size:.8125rem">Name, Kürzel und Farbe pflegst du im Reiter „Fächer“.</div></div>
      </div>` : `
      <div class="lp-seg" role="tablist">
        <button type="button" data-mode="vorhanden"${available.length === 0 ? " disabled title=\"Alle Fächer stehen schon im Lehrplan\"" : ""}>Vorhandenes Fach</button>
        <button type="button" data-mode="neu">Neues Fach</button>
      </div>
      <div class="form-group" id="m-existing">
        <label for="e-subject">Fach *</label>
        <select id="e-subject">
          <option value="">Fach wählen</option>
          ${available.map(s => `<option value="${s.id}">${esc(s.name)}${s.shortName ? ` (${esc(s.shortName)})` : ""}</option>`).join("")}
        </select>
      </div>
      <div class="lp-new-grid" id="m-new">
        <div class="form-group"><label for="e-name">Bezeichnung *</label><input id="e-name" placeholder="z.B. Anatomie (Organe)" /></div>
        <div class="form-group"><label for="e-short">Kürzel</label><input id="e-short" placeholder="AnaO" /></div>
        <div class="form-group"><label for="e-color">Farbe</label><input type="color" id="e-color" value="#2563eb" /></div>
      </div>`;

  const groupOpts = `<option value="">Keine Gruppe</option>` + c.groups
    .map(g => `<option value="${g.id}" ${g.id === entry?.groupId ? "selected" : ""}>${esc(g.name)}${g.pflichtUe !== null ? ` (Pflicht ${fmt(g.pflichtUe)} UE)` : ""}</option>`).join("");

  openModal(isEdit ? "Fach im Lehrplan" : `Fach aufnehmen: ${model.name}`, `<div class="lp-form">
    ${picker}
    <div class="form-grid">
      <div class="form-group">
        <label for="e-group">Fächergruppe</label>
        <select id="e-group">${groupOpts}</select>
      </div>
      <div class="form-group">
        <label for="e-stage">Stufe</label>
        <select id="e-stage">${stageOptions(entry?.stage ?? null)}</select>
      </div>
    </div>
    <div class="lp-ue-grid">
      <div class="form-group">
        <label for="e-schule">UE Schule</label>
        ${ueInput("e-schule", entry?.schuleUe)}
        <small class="text-muted">Daran erkennt der Stundenplan, wann das Fach fertig ist.${entry?.poolUe ? ` Davon ${esc(fmt(entry.poolUe))} UE aus dem Pool.` : ""}</small>
      </div>
      <div class="form-group" id="e-pflicht-field">
        <label for="e-pflicht">Pflicht laut Verordnung</label>
        ${ueInput("e-pflicht", entry?.pflichtUe)}
        <small class="text-muted">Nur, wenn die Verordnung das Fach einzeln vorgibt.</small>
      </div>
      <div class="form-group">
        <label for="e-hpw">UE pro Woche</label>
        ${ueInput("e-hpw", entry?.hoursPerWeek)}
        <small class="text-muted">Optional, für den Vorschlag beim Anlegen einer Serie.</small>
      </div>
    </div>
    <div class="lp-over-note hidden" id="e-over"></div></div>
    ${modalActions(isEdit ? "Aus dem Lehrplan nehmen" : undefined)}`, { wide: true });

  const mb = modalBody();
  const groupSelect = mb.querySelector("#e-group") as HTMLSelectElement;
  const pflichtInput = mb.querySelector("#e-pflicht") as HTMLInputElement;
  let ownPflicht = pflichtInput.value;

  // In einer Gruppe hat das Fach keine eigene Pflicht, das Feld entfällt. Gemeldet wird
  // nur, wenn die Fächer der Gruppe mit diesem Wert zusammen über ihrer Pflicht liegen.
  const schuleInput = mb.querySelector("#e-schule") as HTMLInputElement;
  const syncPflicht = () => {
    const g = c.groups.find(x => x.id === +groupSelect.value);
    mb.querySelector("#e-pflicht-field")!.classList.toggle("hidden", !!g);
    const own = parseUe(schuleInput.value);
    const others = c.subjects.filter(s => s.groupId === g?.id && s.id !== entry?.id).reduce((a, s) => a + (s.schuleUe ?? 0), 0);
    mb.querySelector("#e-over")!.outerHTML = overNote("e-over", g?.name, g?.pflichtUe ?? null,
      others + (typeof own === "number" ? own : 0), "kommt damit auf");
  };
  schuleInput.addEventListener("input", syncPflicht);
  pflichtInput.addEventListener("input", () => { ownPflicht = pflichtInput.value; });
  groupSelect.addEventListener("change", syncPflicht);
  syncPflicht();

  const syncMode = () => {
    mb.querySelectorAll<HTMLButtonElement>(".lp-seg button").forEach(b => b.classList.toggle("is-active", b.dataset.mode === mode));
    mb.querySelector("#m-existing")?.classList.toggle("hidden", mode !== "vorhanden");
    mb.querySelector("#m-new")?.classList.toggle("hidden", mode !== "neu");
  };
  mb.querySelectorAll<HTMLButtonElement>(".lp-seg button").forEach(b => b.addEventListener("click", () => {
    mode = b.dataset.mode as typeof mode;
    syncMode();
    (mb.querySelector(mode === "neu" ? "#e-name" : "#e-subject") as HTMLElement | null)?.focus();
  }));
  syncMode();

  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  if (!canWrite()) { lockInputs(mb); return; }

  mb.querySelector("#m-danger")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Aus dem Lehrplan nehmen",
      message: `„${entry!.name}“ wird aus dem Lehrplan von „${model.name}“ genommen. Das Fach selbst und seine Termine bleiben erhalten.`,
      confirmLabel: "Herausnehmen", danger: true,
    });
    if (!ok) return;
    try {
      apply(await api.stundenplan.curriculum.removeSubject(model.id, entry!.id));
      closeModal();
      showToast("Aus dem Lehrplan genommen");
    } catch (err: any) { showModalError(mb, err.message); }
  });

  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const schule = parseUe((mb.querySelector("#e-schule") as HTMLInputElement).value);
    const pflicht = parseUe(ownPflicht);
    const hpw = parseUe((mb.querySelector("#e-hpw") as HTMLInputElement).value);
    if (schule === "invalid" || pflicht === "invalid" || hpw === "invalid") { showModalError(mb, "UE müssen Zahlen ab 0 sein"); return; }
    const groupId = +groupSelect.value || null;
    const stage = +(mb.querySelector("#e-stage") as HTMLSelectElement).value || null;
    const data: any = { groupId, stage, schuleUe: schule, pflichtUe: groupId ? null : pflicht, hoursPerWeek: hpw };

    if (!isEdit) {
      if (mode === "vorhanden") {
        const id = +(mb.querySelector("#e-subject") as HTMLSelectElement).value;
        if (!id) { showModalError(mb, "Bitte ein Fach wählen"); return; }
        data.subjectId = id;
      } else {
        data.name = (mb.querySelector("#e-name") as HTMLInputElement).value.trim();
        data.shortName = (mb.querySelector("#e-short") as HTMLInputElement).value.trim() || null;
        data.color = (mb.querySelector("#e-color") as HTMLInputElement).value;
        if (!data.name) { showModalError(mb, "Bitte eine Bezeichnung eingeben"); return; }
      }
    }

    setBusy(mb, true);
    try {
      const next = isEdit
        ? await api.stundenplan.curriculum.updateSubject(model.id, entry!.id, data)
        : await api.stundenplan.curriculum.addSubject(model.id, data);
      if (!isEdit && mode === "neu") subjects = await api.stundenplan.subjects.list();
      closeModal();
      expandGroup(groupId);
      apply(next);
      showToast(isEdit ? "Gespeichert" : "Fach aufgenommen");
    } catch (err: any) {
      showModalError(mb, err.message);
      setBusy(mb, false);
    }
  });

  if (!isEdit) (mb.querySelector(mode === "neu" ? "#e-name" : "#e-subject") as HTMLElement | null)?.focus();
}

/**
 * Fächergruppe anlegen oder bearbeiten, samt ihrer Fächer: wer „Anatomie, 240 UE" anlegt,
 * will die Anatomie-Fächer gleich dazunehmen und sehen, ob ihre UE die Pflicht erfüllen.
 */
function openGroupForm(group?: CurriculumGroup) {
  const isEdit = !!group;
  const c = curriculum!;
  const list = [...c.subjects].sort((a, b) => a.name.localeCompare(b.name, "de"));
  const groupName = (id: number | null) => c.groups.find(g => g.id === id)?.name;

  openModal(isEdit ? "Fächergruppe bearbeiten" : "Neue Fächergruppe", `<div class="lp-form">
    <p class="lp-modal-lead">Für Fächer, deren Pflicht die Verordnung gemeinsam vorgibt, etwa „Anatomie“ mit 240 UE für Bewegungsapparat, Organe und Neuro.</p>
    <div class="lp-group-grid">
      <div class="form-group"><label for="g-name">Bezeichnung *</label><input id="g-name" value="${esc(group?.name || "")}" placeholder="z.B. Anatomie" /></div>
      <div class="form-group"><label for="g-pflicht">Pflicht laut Verordnung</label>${ueInput("g-pflicht", group?.pflichtUe)}</div>
    </div>
    <div class="form-group">
      <label>Fächer in dieser Gruppe</label>
      ${list.length === 0
        ? `<div class="lp-muted" style="font-size:.875rem">Noch keine Fächer im Lehrplan. Nimm sie zuerst mit „+ Fach“ auf.</div>`
        : `<div class="lp-checklist">
            ${list.map(s => {
              const other = s.groupId !== null && s.groupId !== group?.id ? groupName(s.groupId) : null;
              return `<label class="lp-check">
                <input type="checkbox" value="${s.id}" ${isEdit && s.groupId === group!.id ? "checked" : ""} />
                ${swatch(s.color)}<span class="lp-check-name">${esc(s.name)}</span>
                ${other ? `<span class="lp-check-note">bisher in „${esc(other)}“</span>` : ""}
                <span class="lp-check-ue">${s.schuleUe !== null ? `${fmt(s.schuleUe)} UE` : ""}</span>
              </label>`;
            }).join("")}
          </div>
          <div class="lp-check-sum" id="g-sum"></div>`}
    </div></div>
    ${modalActions(isEdit ? "Gruppe löschen" : undefined)}`, { wide: true });

  const mb = modalBody();
  const pflichtInput = mb.querySelector("#g-pflicht") as HTMLInputElement;
  const boxes = () => Array.from(mb.querySelectorAll<HTMLInputElement>(".lp-checklist input"));

  const updateSum = () => {
    const sumBox = mb.querySelector("#g-sum") as HTMLElement | null;
    if (!sumBox) return;
    const chosen = boxes().filter(b => b.checked).map(b => c.subjects.find(s => s.id === +b.value)!);
    const sum = chosen.reduce((a, s) => a + (s.schuleUe ?? 0), 0);
    const p = parseUe(pflichtInput.value);
    const pflicht = typeof p === "number" ? p : null;
    sumBox.innerHTML = `${chosen.length} ${chosen.length === 1 ? "Fach" : "Fächer"}, zusammen <strong>${fmt(sum)} UE</strong>`
      + (pflicht !== null ? ` von ${fmt(pflicht)} UE Pflicht ${statusBadge(sum, pflicht)}` : "");
  };
  mb.addEventListener("change", updateSum);
  pflichtInput.addEventListener("input", updateSum);
  updateSum();

  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  if (!canWrite()) { lockInputs(mb); return; }

  mb.querySelector("#m-danger")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Fächergruppe löschen",
      message: `„${group!.name}“ wird gelöscht. Ihre Fächer bleiben im Lehrplan, stehen dann aber ohne Gruppe da.`,
      confirmLabel: "Löschen", danger: true,
    });
    if (!ok) return;
    try {
      apply(await api.stundenplan.curriculum.deleteGroup(model.id, group!.id));
      closeModal();
      showToast("Fächergruppe gelöscht");
    } catch (err: any) { showModalError(mb, err.message); }
  });

  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const name = (mb.querySelector("#g-name") as HTMLInputElement).value.trim();
    const pflicht = parseUe(pflichtInput.value);
    if (!name) { showModalError(mb, "Bitte eine Bezeichnung eingeben"); return; }
    if (pflicht === "invalid") { showModalError(mb, "Die Pflicht muss eine Zahl ab 0 sein"); return; }

    setBusy(mb, true);
    try {
      let view = isEdit
        ? await api.stundenplan.curriculum.updateGroup(model.id, group!.id, { name, pflichtUe: pflicht })
        : await api.stundenplan.curriculum.createGroup(model.id, { name, pflichtUe: pflicht });
      // Die neue Gruppe ist die mit der höchsten Id unter diesem Namen.
      const gid = isEdit ? group!.id : Math.max(...view.groups.filter(g => g.name === name).map(g => g.id));

      // Nur geänderte Zuordnungen schreiben. Ein Fach in einer Gruppe hat keine eigene Pflicht.
      for (const box of boxes()) {
        const s = view.subjects.find(x => x.id === +box.value);
        if (!s) continue;
        if (box.checked === (s.groupId === gid)) continue;
        view = await api.stundenplan.curriculum.updateSubject(model.id, s.id, {
          groupId: box.checked ? gid : null, stage: s.stage, schuleUe: s.schuleUe, pflichtUe: null, hoursPerWeek: s.hoursPerWeek,
        });
      }
      closeModal();
      if (!isEdit) expandGroup(gid);
      apply(view);
      showToast("Fächergruppe gespeichert");
    } catch (err: any) {
      showModalError(mb, err.message);
      setBusy(mb, false);
    }
  });

  (mb.querySelector("#g-name") as HTMLInputElement).focus();
}

function openTotalForm() {
  openModal("Umfang laut Verordnung", `<div class="lp-form">
    <p class="lp-modal-lead">Der Mindestumfang der Ausbildung laut Ausbildungs- und Prüfungsverordnung, in der Physiotherapie zum Beispiel 2900 UE. Gibt die Verordnung zusätzlich Pool-Stunden vor, verteilt ihr sie mit „Pool-Stunden vergeben“ auf Fächer.</p>
    <div class="form-grid">
      <div class="form-group">
        <label for="t-total">Gesamtumfang</label>
        ${ueInput("t-total", curriculum!.totalUe)}
      </div>
      <div class="form-group">
        <label for="t-pool">Pool-Stunden</label>
        ${ueInput("t-pool", curriculum!.poolUe)}
      </div>
    </div></div>
    ${modalActions()}`);
  const mb = modalBody();
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  const input = mb.querySelector("#t-total") as HTMLInputElement;
  input.focus();
  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const value = parseUe(input.value);
    const pool = parseUe((mb.querySelector("#t-pool") as HTMLInputElement).value);
    if (value === "invalid" || pool === "invalid") { showModalError(mb, "Bitte Zahlen ab 0 eingeben"); return; }
    setBusy(mb, true);
    try {
      apply(await api.stundenplan.curriculum.setTotal(model.id, value, pool));
      closeModal();
      showToast("Umfang gespeichert");
    } catch (err: any) { showModalError(mb, err.message); setBusy(mb, false); }
  });
}

/**
 * Pool-Stunden auf Fächer verteilen. Angehakte Fächer bekommen ein UE-Feld; die Summe darf
 * den Pool nicht überschreiten. Wer darüber hinaus individualisieren will, ändert UE Schule
 * am Fach selbst. afterTemplate: direkt nach „Vorlage übernehmen“ geöffnet.
 */
function openPoolForm(afterTemplate?: string) {
  const c = curriculum!;
  const pool = c.poolUe ?? 0;
  const groupName = new Map(c.groups.map(g => [g.id, g.name]));
  const list = [...c.subjects].sort((a, b) => a.name.localeCompare(b.name, "de"));
  const lead = afterTemplate
    ? `Die Vorlage „${esc(afterTemplate)}“ enthält <strong>${fmt(pool)} UE Pool-Stunden</strong>. Wähle die Fächer, die davon etwas bekommen, und trage die UE ein. Sie kommen zu „UE Schule“ des Fachs hinzu.`
    : `Wähle die Fächer, die Pool-Stunden bekommen, und trage die UE ein. Sie kommen zu „UE Schule“ des Fachs hinzu. Mehr als <strong>${fmt(pool)} UE</strong> lassen sich nicht vergeben; für Abweichungen darüber hinaus änderst du „UE Schule“ am Fach.`;

  openModal("Pool-Stunden vergeben", `<div class="lp-form">
    <p class="lp-modal-lead">${lead}</p>
    ${list.length === 0
      ? `<div class="lp-muted" style="font-size:.875rem">Noch keine Fächer im Lehrplan.</div>`
      : `<div class="form-group lp-pool-search"><input type="search" id="p-search" placeholder="Fach suchen" aria-label="Fach suchen" /></div>
        <div class="lp-checklist lp-pool-list">
          ${list.map(s => `
            <div class="lp-check lp-pool-row" data-entry="${s.id}" data-name="${esc(s.name.toLowerCase())}">
              <label class="lp-pool-pick">
                <input type="checkbox" value="${s.id}" ${s.poolUe ? "checked" : ""} />
                ${swatch(s.color)}<span class="lp-check-name">${esc(s.name)}${s.groupId !== null ? ` <span class="lp-pool-group">${esc(groupName.get(s.groupId) ?? "")}</span>` : ""}</span>
              </label>
              <span class="lp-check-ue" title="UE Schule ohne Pool">${fmt((s.schuleUe ?? 0) - (s.poolUe ?? 0))} UE</span>
              <div class="form-group lp-pool-ue">${ueInput(`p-ue-${s.id}`, s.poolUe, `class="lp-pool-input"${s.poolUe ? "" : " disabled"} aria-label="Pool-Stunden für ${esc(s.name)}"`)}</div>
            </div>`).join("")}
        </div>
        <div class="lp-check-sum" id="p-sum"></div>`}
    </div>
    ${modalActions(undefined, "Speichern", afterTemplate ? "Später vergeben" : "Abbrechen")}`,
  { wide: true, stickyActions: true });

  const mb = modalBody();
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  const rows = Array.from(mb.querySelectorAll<HTMLElement>(".lp-pool-row"));
  const box = (r: HTMLElement) => r.querySelector<HTMLInputElement>("input[type=checkbox]")!;
  const ue = (r: HTMLElement) => r.querySelector<HTMLInputElement>(".lp-pool-input")!;

  /** Verteilung laut Formular; null bei einer ungültigen Eingabe. */
  const read = (): { entryId: number; poolUe: number }[] | null => {
    const out: { entryId: number; poolUe: number }[] = [];
    for (const r of rows) {
      if (!box(r).checked) continue;
      const v = parseUe(ue(r).value);
      if (v === "invalid") return null;
      if (v) out.push({ entryId: +r.dataset.entry!, poolUe: v });
    }
    return out;
  };

  const update = () => {
    const sumBox = mb.querySelector("#p-sum") as HTMLElement | null;
    const save = mb.querySelector("#m-save") as HTMLButtonElement | null;
    const alloc = read();
    const sum = (alloc ?? []).reduce((a, x) => a + x.poolUe, 0);
    const over = sum - pool > 0.001;
    if (sumBox) {
      sumBox.innerHTML = alloc === null
        ? `<span class="lp-bad">Bitte nur Zahlen ab 0 eintragen</span>`
        : over
          ? `<strong>${fmt(sum)} von ${fmt(pool)} UE</strong> vergeben <span class="lp-bad">${fmt(sum - pool)} UE mehr als im Pool</span>`
          : `<strong>${fmt(sum)} von ${fmt(pool)} UE</strong> vergeben, ${pool - sum > 0.001 ? `${fmt(pool - sum)} UE offen` : `<span class="lp-good">alles vergeben</span>`}`;
    }
    if (save) save.disabled = alloc === null || over;
  };

  rows.forEach(r => {
    box(r).addEventListener("change", () => {
      ue(r).disabled = !box(r).checked;
      if (box(r).checked) ue(r).focus(); else ue(r).value = "";
      update();
    });
    ue(r).addEventListener("input", update);
  });
  mb.querySelector("#p-search")?.addEventListener("input", (e) => {
    const q = (e.target as HTMLInputElement).value.trim().toLowerCase();
    rows.forEach(r => r.classList.toggle("hidden", !!q && !r.dataset.name!.includes(q)));
  });
  update();

  if (!canWrite()) { lockInputs(mb); return; }
  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const alloc = read();
    if (alloc === null) return;
    setBusy(mb, true);
    try {
      apply(await api.stundenplan.curriculum.setPool(model.id, alloc));
      closeModal();
      showToast("Pool-Stunden vergeben");
    } catch (err: any) {
      showModalError(mb, err.message);
      setBusy(mb, false);
      update();
    }
  });
}

async function openTemplateDialog() {
  let templates: CurriculumTemplateInfo[];
  try { templates = await api.stundenplan.curriculum.templates(model.id); }
  catch (err: any) { showToast("Fehler: " + err.message); return; }

  if (templates.length === 0) {
    openModal("Vorlage übernehmen", `
      <p class="lp-modal-lead">Für Lehrpläne gibt es noch keine Vorlagen. Lege den Lehrplan selbst an, kopiere ihn von einem anderen Modell oder ordne Fächer im Reiter „Fächer“ zu.</p>
      <div class="form-actions"><button class="btn btn-secondary" id="m-cancel">Schließen</button></div>`);
    modalBody().querySelector("#m-cancel")!.addEventListener("click", closeModal);
    return;
  }

  openModal("Vorlage übernehmen", `<div class="lp-form">
    <p class="lp-modal-lead">Übernimmt Fächergruppen, Fächer und Pflicht-UE in den Lehrplan von <strong>${esc(model.name)}</strong>. Was schon im Lehrplan steht, bleibt unverändert; fehlende Fächer werden angelegt. Die Stufen trägst du danach ein.</p>
    <div class="lp-options">
      ${templates.map((t, i) => `
        <label class="lp-option">
          <input type="radio" name="t-choice" value="${t.id}" ${i === 0 ? "checked" : ""} />
          <span class="lp-option-body">
            <span class="lp-option-title">${esc(t.name)}${t.passend ? ` <span class="badge badge-green">passt</span>` : ""}</span>
            <span class="lp-option-meta">${[t.departmentGroupName, `${t.subjectCount} Fächer`, t.totalUe !== null ? `${fmt(t.totalUe)} UE gesamt` : "", t.poolUe ? `${fmt(t.poolUe)} UE Pool` : ""].filter(Boolean).map(esc).join(" · ")}</span>
          </span>
        </label>`).join("")}
    </div></div>
    ${modalActions(undefined, "Übernehmen")}`);

  const mb = modalBody();
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const choice = mb.querySelector<HTMLInputElement>("input[name=t-choice]:checked");
    if (!choice) return;
    setBusy(mb, true, "Übernehmen");
    try {
      const result = await api.stundenplan.curriculum.applyTemplate(model.id, +choice.value);
      subjects = await api.stundenplan.subjects.list();
      closeModal();
      apply(result.view);
      const parts = [`${result.imported} Fächer übernommen`];
      if (result.created) parts.push(`${result.created} neu angelegt`);
      if (result.skipped) parts.push(`${result.skipped} standen schon im Lehrplan`);
      showToast(parts.join(", "));
      // Bringt die Vorlage Pool-Stunden mit, gleich zum Verteilen weiter. Wer später
      // vergibt, behält den Hinweis über der Tafel.
      const chosen = templates.find(t => t.id === +choice.value);
      if (chosen?.poolUe && poolOpen(result.view)) openPoolForm(chosen.name);
    } catch (err: any) {
      showModalError(mb, err.message);
      setBusy(mb, false, "Übernehmen");
    }
  });
}

function openCopyDialog() {
  const others = models.filter(m => m.id !== model.id);
  const withPlan = others.filter(m => m.curriculum.subjectCount > 0);
  if (withPlan.length === 0) {
    openModal("Lehrplan kopieren", `
      <p class="lp-modal-lead">Kein anderes Ausbildungsmodell hat bisher einen Lehrplan.</p>
      <div class="form-actions"><button class="btn btn-secondary" id="m-cancel">Schließen</button></div>`);
    modalBody().querySelector("#m-cancel")!.addEventListener("click", closeModal);
    return;
  }
  openModal("Lehrplan kopieren", `<div class="lp-form">
    <p class="lp-modal-lead">Übernimmt Gruppen, Fächer und UE eines anderen Modells, etwa für Teilzeit aus Vollzeit. Was schon im Lehrplan von <strong>${esc(model.name)}</strong> steht, bleibt unverändert. Stufen kommen mit, sofern es sie hier gibt; sonst stehen die Fächer auf „alle Stufen“.</p>
    <div class="lp-options">
      ${withPlan.map((m, i) => `
        <label class="lp-option">
          <input type="radio" name="c-choice" value="${m.id}" ${i === 0 ? "checked" : ""} />
          <span class="lp-option-body">
            <span class="lp-option-title">${esc(m.name)}</span>
            <span class="lp-option-meta">${m.curriculum.subjectCount} Fächer${m.curriculum.schuleUe !== null ? ` · ${esc(fmt(m.curriculum.schuleUe))} UE Schule` : ""} · ${m.stageCount} × ${esc(m.stageNoun)}</span>
          </span>
        </label>`).join("")}
    </div></div>
    ${modalActions(undefined, "Kopieren")}`);
  const mb = modalBody();
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#m-save")!.addEventListener("click", async () => {
    const choice = mb.querySelector<HTMLInputElement>("input[name=c-choice]:checked");
    if (!choice) return;
    setBusy(mb, true, "Kopieren");
    try {
      const result = await api.stundenplan.curriculum.copyFrom(model.id, +choice.value);
      closeModal();
      apply(result.view);
      showToast(`${result.imported} Fächer übernommen${result.skipped ? `, ${result.skipped} standen schon im Lehrplan` : ""}`);
    } catch (err: any) {
      showModalError(mb, err.message);
      setBusy(mb, false, "Kopieren");
    }
  });
}

async function clearCurriculum() {
  const ok = await confirmDialog({
    title: "Lehrplan leeren",
    message: `Alle Fächer und Gruppen werden aus dem Lehrplan von „${model.name}“ genommen. Die Fächer selbst und ihre Termine bleiben erhalten, „Fach fertig“ entfällt bis zum neuen Lehrplan.`,
    confirmLabel: "Leeren", danger: true,
  });
  if (!ok) return;
  try {
    apply(await api.stundenplan.curriculum.clear(model.id));
    showToast("Lehrplan geleert");
  } catch (err: any) { showToast("Fehler: " + err.message); }
}

async function move(type: "gruppe" | "fach", id: number, dir: number) {
  const c = curriculum!;
  const top = topLevel(c);
  const byGroup = new Map(c.groups.map(g => [g.id, members(c, g.id)]));
  const entry = type === "fach" ? c.subjects.find(s => s.id === id) : null;
  if (entry && entry.groupId !== null) {
    const list = byGroup.get(entry.groupId)!;
    const i = list.findIndex(s => s.id === id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
  } else {
    const i = top.findIndex(t => t.type === type && (t.type === "gruppe" ? t.group.id : t.subject.id) === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= top.length) return;
    [top[i], top[j]] = [top[j], top[i]];
  }
  const items: { type: "gruppe" | "fach"; id: number }[] = [];
  for (const t of top) {
    if (t.type === "fach") { items.push({ type: "fach", id: t.subject.id }); continue; }
    items.push({ type: "gruppe", id: t.group.id });
    for (const s of byGroup.get(t.group.id) ?? []) items.push({ type: "fach", id: s.id });
  }
  try { apply(await api.stundenplan.curriculum.reorder(model.id, items)); }
  catch (err: any) { showToast("Fehler: " + err.message); }
}

/**
 * Lehrplan eines Modells in el zeichnen. onChange läuft nach jeder Änderung, damit die
 * Seite ihre Kennzahlen (etwa in der Modellliste) nachziehen kann.
 */
export async function renderCurriculumEditor(
  el: HTMLElement, forModel: CourseModel, allModels: CourseModel[], onChange: () => void = () => {},
  actionsEl: HTMLElement | null = null
): Promise<void> {
  actionsHost = actionsEl;
  // Jedes Öffnen der Seite beginnt mit allen Gruppen aufgeklappt.
  expanded = null;
  model = forModel;
  models = allModels;
  changed = onChange;
  el.innerHTML = `<div class="lp-page"><div style="display:flex;align-items:center;justify-content:center;height:160px"><div class="spinner"></div></div></div>`;
  host = el.querySelector(".lp-page") as HTMLElement;
  try {
    [curriculum, subjects] = await Promise.all([
      api.stundenplan.curriculum.get(model.id),
      api.stundenplan.subjects.list(),
    ]);
  } catch (err: any) {
    host.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  render();

  // Ein Listener je Aufruf, an frisch erzeugten Elementen: er stirbt mit ihnen.
  actionsHost?.addEventListener("click", onClick);
  host.addEventListener("click", onClick);
  // Das Menü schließt bei Klick daneben und mit Escape. Einmal je Sitzung reicht.
  if (!menuListener) {
    menuListener = true;
    document.addEventListener("click", (e) => { if (!(e.target as HTMLElement).closest?.(".lp-more")) closeMenus(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeMenus(); });
  }
}

function onClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  if (!curriculum) return;
  const action = (target.closest("[data-action]") as HTMLElement | null)?.dataset.action;
  if (action === "more") {
    const wrap = target.closest(".lp-more")!;
    const panel = wrap.querySelector(".lp-more-panel")!;
    const willOpen = panel.classList.contains("hidden");
    closeMenus();
    if (willOpen) {
      panel.classList.remove("hidden");
      wrap.querySelector('[data-action="more"]')!.setAttribute("aria-expanded", "true");
    }
    return;
  }
  if (action) closeMenus();

  const toggle = target.closest("[data-toggle]") as HTMLElement | null;
  if (toggle) {
    const ids = expandedIds(curriculum);
    const id = +toggle.dataset.toggle!;
    if (ids.has(id)) ids.delete(id); else ids.add(id);
    render();
    return;
  }
  if (action === "template") { openTemplateDialog(); return; }
  if (action === "copy") { openCopyDialog(); return; }
  if (action === "group") { openGroupForm(); return; }
  if (action === "subject") { openEntryForm(); return; }
  if (action === "total") { openTotalForm(); return; }
  if (action === "pool") { openPoolForm(); return; }
  if (action === "clear") { clearCurriculum(); return; }

  const mv = target.closest("[data-move]") as HTMLElement | null;
  if (mv) {
    const [type, id] = mv.dataset.move!.split(":");
    move(type as "gruppe" | "fach", +id, +mv.dataset.dir!);
    return;
  }
  const groupRow = target.closest("tr[data-group]") as HTMLElement | null;
  if (groupRow) {
    const g = curriculum.groups.find(x => x.id === +groupRow.dataset.group!);
    if (g) openGroupForm(g);
    return;
  }
  const entryRow = target.closest("tr[data-entry]") as HTMLElement | null;
  if (entryRow) {
    const s = curriculum.subjects.find(x => x.id === +entryRow.dataset.entry!);
    if (s) openEntryForm(s);
  }
}
